import { FlexBox, IconButton, Switch, Table } from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import { Satisfaction } from 'lib-shared-legacy-types';
import { useCallback, useEffect, useState } from 'react';

import { ratingApi } from 'apis/rating';
import { appActions } from 'app/app.actions';
import { languages } from 'app/utils/supportedLanguages';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

type Props = {
  languageSettings: Satisfaction.CsatSettings;
  handleGoToLanguage: (languageCode: string) => void;
  onDelete: () => void;
};

export const SurveyLanguageTableRow = (props: Props): JSX.Element => {
  const [isEnabled, setIsEnabled] = useState(props.languageSettings.enabled);

  const [updateRatingSettings] = ratingApi.useLazyUpdateRatingSettingsQuery();

  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const [deleteRatingSettings] = ratingApi.useLazyDeleteRatingSettingsQuery();

  const openDeleteModal = useCallback(async () => {
    const { isConfirmed } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: 'Delete language?',
            description: 'The CSAT language customization will be lost.',
            confirm: {
              label: 'Delete',
            },
          },
        },
      }),
    );

    if (isConfirmed) {
      try {
        const deleteResponse = await deleteRatingSettings({
          ratingSettingsId: props.languageSettings.id,
        }).unwrap();

        if (isResponseWithData(deleteResponse)) {
          props.onDelete();
        }

        notify(
          {
            title: 'Success',
            text: `The language customization has been deleted.`,
            icon: 'done',
            time: 5000,
          },
          NotificationType.Success,
        );
      } catch (error) {
        notify(
          {
            title: 'Error while deleting language customization.',
          },
          NotificationType.Error,
        );
      }
    }
  }, [deleteRatingSettings, dispatch, notify, props]);

  const handleOnEnabledChange = useCallback(
    async (enabled: boolean) => {
      await updateRatingSettings({
        id: props.languageSettings.id,
        enabled: enabled,
      });
      setIsEnabled(enabled);
    },
    [props.languageSettings.id, updateRatingSettings],
  );

  useEffect(() => {
    setIsEnabled(props.languageSettings.enabled);
  }, [props.languageSettings.enabled, setIsEnabled]);

  const languageLabel = languages
    .filter(({ code }) => code == props.languageSettings.language)
    .map(({ label }) => label)
    .shift();

  return (
    <Table.Tr>
      <Table.Td>{languageLabel ?? '-'}</Table.Td>

      <Table.Td align="center">
        <Switch
          name="enable-language"
          value="enable-language"
          checked={isEnabled}
          onChange={(e) => {
            handleOnEnabledChange(e.target.checked);
          }}
        />
      </Table.Td>

      <Table.Td>
        <FlexBox flexDirection="row">
          <IconButton
            variant="tertiary"
            icon="pen"
            onClick={() =>
              props.handleGoToLanguage(props.languageSettings.language ?? 'en')
            }
          />
          <IconButton
            variant="tertiary"
            icon="trash"
            onClick={openDeleteModal}
          />
        </FlexBox>
      </Table.Td>
    </Table.Tr>
  );
};
